import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PAYOUT_NOTE_MAX } from "@/schemas/payout";
import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
} from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { Money, CircleNotch, Info, Warning } from "@phosphor-icons/react";
import { formatMoney } from "@/lib/admin-payment-breakdown";

import { groupSessionsByTherapist, itemSummary, payoutPreview } from "./payout-helpers";
import { PayoutAvatar } from "./primitives";

import type { UnpaidSession } from "@/types";
import type { WithholdingRates } from "@/lib/money/work-contract";

interface CreatePayoutDialogProps {
  sessions: UnpaidSession[];
  /** Stope obustava sa servera; bez njih se neto za ugovor o djelu ne prikazuje. */
  withholdingRates: WithholdingRates | null;
  note: string;
  onNoteChange: (v: string) => void;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export function CreatePayoutDialog({
  sessions,
  withholdingRates,
  note,
  onNoteChange,
  isPending,
  onConfirm,
  onClose,
}: CreatePayoutDialogProps) {
  const groups = groupSessionsByTherapist(sessions).map((group) => ({
    ...group,
    preview: payoutPreview(group.sessions, withholdingRates),
  }));
  const multi = groups.length > 1;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const totalGross = round2(groups.reduce((sum, g) => sum + g.preview.gross, 0));
  const anyWithholding = groups.some((g) => g.preview.workContract);
  const netKnown = groups.every((g) => g.preview.net != null);
  const totalWithheld = round2(groups.reduce((sum, g) => sum + (g.preview.totalWithheld ?? 0), 0));
  const totalNet = round2(groups.reduce((sum, g) => sum + (g.preview.net ?? 0), 0));
  const missingEarnings = groups.reduce((sum, g) => sum + g.preview.missingEarnings, 0);

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <AdminDialogHeading
          icon={Money}
          title={multi ? "Potvrda isplata" : "Potvrda isplate"}
          description="Pregledajte iznose prije kreiranja isplate."
          tone="success"
        />
      </DialogHeader>

      <DialogBody>
        <div className="flex flex-col gap-4">
          {multi && (
            <div className="flex items-start gap-2.5 rounded-xl bg-primary/5 px-3.5 py-3 text-[13px] leading-snug text-foreground ring-1 ring-primary/15">
              <Info className="mt-0.5 size-4 shrink-0 text-primary" />
              <p>
                Odabrane stavke pripadaju različitim terapeutima, pa će se
                kreirati {groups.length} odvojene isplate.
              </p>
            </div>
          )}

          {missingEarnings > 0 && (
            <div className="flex items-start gap-2.5 rounded-xl bg-destructive/5 px-3.5 py-3 text-[13px] leading-snug text-foreground ring-1 ring-destructive/20">
              <Warning className="mt-0.5 size-4 shrink-0 text-destructive" />
              <p>
                {missingEarnings === 1
                  ? "Jedna odabrana stavka nema obračunatu zaradu terapeuta."
                  : `Odabrane stavke bez obračunate zarade: ${missingEarnings}.`}{" "}
                Ispravite obračun ili ih izbacite iz odabira prije isplate.
              </p>
            </div>
          )}

          {/* Per-therapist breakdown */}
          <div className="overflow-hidden rounded-xl border border-border/60">
            <ul className="divide-y divide-border/40">
              {groups.map((group) => (
                <li
                  key={group.therapistId}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  <PayoutAvatar name={group.therapistName} tone="primary" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground">
                      {group.therapistName}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {itemSummary(group.sessions)}
                    </p>
                    {group.preview.workContract && (
                      <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                        {group.preview.totalWithheld != null
                          ? `Ugovor o djelu: bruto ${formatMoney(group.preview.gross)} KM, obustave ${formatMoney(group.preview.totalWithheld)} KM`
                          : "Ugovor o djelu: obustave se obračunavaju pri isplati"}
                      </p>
                    )}
                  </div>
                  <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                    {formatMoney(group.preview.net ?? group.preview.gross)}{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      KM
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            {anyWithholding && netKnown && (
              <dl className="space-y-1 border-t border-border/60 px-4 py-2.5 text-[13px]">
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Bruto</dt>
                  <dd className="tabular-nums text-foreground">{formatMoney(totalGross)} KM</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-muted-foreground">Obustave (porez i doprinosi)</dt>
                  <dd className="tabular-nums text-foreground">{formatMoney(totalWithheld)} KM</dd>
                </div>
              </dl>
            )}
            <div className="flex items-center justify-between border-t border-border/60 bg-muted/30 px-4 py-3">
              <span className="text-sm text-muted-foreground">
                {anyWithholding && netKnown ? "Za isplatu na račun" : "Ukupan iznos"}
              </span>
              <span
                className="text-lg font-bold tabular-nums text-foreground"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {formatMoney(anyWithholding && netKnown ? totalNet : totalGross)}{" "}
                <span className="text-xs font-normal text-muted-foreground">
                  KM
                </span>
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="payout-note"
              className="text-sm font-medium text-foreground"
            >
              Napomena (opciono)
            </label>
            <Textarea
              id="payout-note"
              value={note}
              maxLength={PAYOUT_NOTE_MAX}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Dodatne napomene uz isplatu..."
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              {multi
                ? "Napomena se dodaje na svaku kreiranu isplatu i šalje terapeutu uz potvrdu."
                : "Napomena se šalje terapeutu uz potvrdu isplate."}
            </p>
          </div>
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Otkaži
        </Button>
        <Button onClick={onConfirm} disabled={isPending || missingEarnings > 0}>
          {isPending ? (
            <>
              <CircleNotch className="size-4 animate-spin" />
              Kreiranje...
            </>
          ) : (
            <>
              <Money className="size-4" />
              {multi ? "Potvrdi isplate" : "Potvrdi isplatu"}
            </>
          )}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
